import {ReactElement} from 'react';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import {Box, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import ResourceCard from 'components/shared/tray/ResourceCard';
import TraySummary from 'components/shared/tray/TraySummary';
import {Section, Tray as TrayInterface} from 'interfaces/Project';

type Props = {
  tray: TrayInterface;
  section: Section;
};

const iconSx = {fontSize: 30, color: 'ctaDark'};

const gridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    lg: 'repeat(3, minmax(0, 1fr))'
  },
  gap: 1.5
};

const selectedTrayHeadingSx = {fontSize: 20, fontWeight: 500, lineHeight: 1.5, color: 'ctaDark'};

const sectionHeadingSx = {fontSize: 18, fontWeight: 500, lineHeight: 1.5, color: 'text.primary'};

export default function Tray({tray, section}: Props): ReactElement {
  const {t} = useTranslation('common');
  const totalSection = section.totalSection === 0 ? tray.totalSection : section.totalSection;
  const length = 3 * Math.ceil(section.length / 3);
  const name = `${tray.name} (${length} ${t('common:meters')})`;

  const fileIcon = <ErrorIcon sx={iconSx} />;

  const documentation = [
    ...(tray.url
      ? [
          {
            key: 'url',
            icon: <DesktopWindowsOutlinedIcon sx={iconSx} />,
            name: t('common:onlineCatalog'),
            value: tray.url
          }
        ]
      : []),
    ...tray.catalogs
      .filter((catalog) => catalog.link)
      .map((catalog) => ({
        key: `catalog-${catalog.id}`,
        icon: <DesktopWindowsOutlinedIcon sx={iconSx} />,
        name: catalog.name || t('common:pdfCatalog'),
        value: catalog.link
      })),
    ...tray.videos
      .filter((video) => video.link)
      .map((video) => ({
        key: `video-${video.id}`,
        icon: <PlayCircleOutlineIcon sx={iconSx} />,
        name: video.name || video.reference || t('common:videos'),
        value: video.link
      }))
  ];

  const technicalFiles = [
    {key: 'rvt', name: t('common:bim'), value: tray.rvt},
    {key: 'acae', name: t('common:workItem'), value: tray.acae},
    {key: 'bc3', name: t('common:workItemBc3'), value: tray.bc3},
    {key: 'xls', name: t('common:workItemXls'), value: tray.xls}
  ].filter((file) => file.value);

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
      <Typography sx={selectedTrayHeadingSx}>{t('common:selectedTray')}</Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          p: 1.5,
          bgcolor: 'sectionBackground',
          border: '1px solid',
          borderColor: 'borderColor',
          borderRadius: 1
        }}
      >
        <TraySummary tray={tray} name={name} totalSection={totalSection} t={t} />

      {documentation.length > 0 && (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
          <Typography sx={sectionHeadingSx}>{t('common:documentation')}</Typography>
          <Box sx={gridSx}>
            {documentation.map((item) => (
              <ResourceCard key={item.key} icon={item.icon} name={item.name} value={item.value} t={t} />
            ))}
          </Box>
        </Box>
      )}

      {technicalFiles.length > 0 && (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
          <Typography sx={sectionHeadingSx}>{t('common:technicalFiles')}</Typography>
          <Box sx={gridSx}>
            {technicalFiles.map((item) => (
              <ResourceCard key={item.key} icon={fileIcon} name={item.name} value={item.value} t={t} />
            ))}
          </Box>
        </Box>
      )}
      </Box>
    </Box>
  );
}
